import { Entity } from '@api/api/entity-schema';
import {
  ColorScheme,
  colorSchemes,
  DEFAULT_THEME,
  ThemeName,
  ThemeVariant,
  themeVariants,
} from '@design/design-tokens/themes/theme-names';
import {
  THEME_NAME_STORAGE_KEY,
  useRemoteStorage,
} from '@terminal/hooks/use-remote-storage';
import { create } from '@terminal/utils/store/createStore';
import { persist } from 'zustand/middleware';

const isValidThemeName = (value: unknown): value is ThemeName =>
  typeof value === 'string' &&
  colorSchemes.some(scheme =>
    themeVariants.some(variant => value === `${scheme}_${variant}`)
  );

const getValidThemeName = (themeName: unknown): ThemeName =>
  isValidThemeName(themeName) ? themeName : DEFAULT_THEME;

const getPersistedThemeName = (): ThemeName => {
  try {
    const raw = localStorage.getItem(THEME_NAME_STORAGE_KEY);

    if (raw) {
      const parsed = JSON.parse(raw);
      return getValidThemeName(parsed?.state?.themeName);
    }
  } catch {}

  return DEFAULT_THEME;
};

const applyThemeToDOM = (themeName: ThemeName, colorScheme: ColorScheme) => {
  document.documentElement.setAttribute('data-theme', themeName);

  if (colorScheme === ColorScheme.Dark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

const initialThemeName = getPersistedThemeName();
const initialColorScheme = initialThemeName.split('_')[0] as ColorScheme;

applyThemeToDOM(initialThemeName, initialColorScheme);

interface ThemeStore {
  themeName: ThemeName;
  colorScheme: ColorScheme;
  initStore: () => void;
  changeTheme: (colorScheme: ColorScheme, themeVariant: ThemeVariant) => void;
  getThemedLogoUrl: (entity: Entity) => string;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      themeName: initialThemeName,
      colorScheme: initialColorScheme,
      initStore: () => {
        const { items } = useRemoteStorage.getState();
        const themeName = getValidThemeName(items?.[THEME_NAME_STORAGE_KEY]);
        const colorScheme = themeName.split('_')[0] as ColorScheme;

        applyThemeToDOM(themeName, colorScheme);
        set({ themeName, colorScheme });
      },
      changeTheme: (colorScheme, themeVariant) => {
        const { storeItems } = useRemoteStorage.getState();
        const themeName = `${colorScheme}_${themeVariant}` as ThemeName;

        applyThemeToDOM(themeName, colorScheme);
        set({ themeName, colorScheme });

        storeItems({
          [THEME_NAME_STORAGE_KEY]: themeName,
        });
      },
      getThemedLogoUrl: (entity: Entity) => {
        return get().colorScheme === ColorScheme.Dark
          ? entity.logo_urls.light_png
          : entity.logo_urls.dark_png;
      },
    }),
    {
      name: THEME_NAME_STORAGE_KEY,
      partialize: state => ({ themeName: state.themeName }),
    }
  )
);
